import { FC, useEffect } from 'react';
import styled from 'styled-components';

import Container from '@/components/Container/Container';
import Seo from '@/components/Seo/Seo';
import { LANGUAGES } from '@/lib/constants';
import { fetchAPI } from '@/lib/fetchAPI';
import { parseHTML } from '@/lib/parse';
import { translate } from '@/lib/translate';
import Partner from '@/modules/messaging-partners/pop-up/PartnerNew';

const MessagingPartnerPage: FC<any> = ({ url, lang, data }) => {
	useEffect(() => {
		document.body.style.overflow = 'hidden';
	}, []);

	return (
		<Page>
			<Seo
				url={'/messaging-partners/' + url}
				title={
					translate(lang, {
						en: 'Viber Messaging Partners',
						ua: 'Партнери з обміну повідомленнями Viber',
						ru: 'Партнеры по обмену сообщениями Viber',
					}) +
					' | ' +
					parseHTML(data?.company)
				}
				description={translate(lang, {
					en: 'Would you like to work with Viber and send business messages to your engaged audience? To get started, reach out to our partners directly.',
					ua: 'Ви хотіли б співпрацювати з Viber і надсилати ділові повідомлення вашій зацікавленій аудиторії? Для початку зверніться безпосередньо до наших партнерів.',
					ru: 'Хотели бы вы работать с Viber и отправлять деловые сообщения своей заинтересованной аудитории? Для начала обратитесь к нашим партнерам напрямую.',
				})}
			/>

			<Wrapper>
				<Gradient
					loading="lazy"
					alt="Gradient"
					src="/images/messaging-partners/Gradient.png"
				/>

				<Container>
					<ContentWrapper>
						<Partner lang={lang} data={data} />
					</ContentWrapper>
				</Container>
			</Wrapper>
		</Page>
	);
};

const Gradient = styled.img`
	position: absolute;
	left: 0;
	top: 0;
	pointer-events: none;

	@media (width <= 800px) {
		display: none;
	}
`;

const Page = styled.div`
	position: relative;
	max-width: 100%;
	display: block;
	overflow: hidden;
`;

const Wrapper = styled.div`
	margin-top: 120px;
	margin-bottom: 80px;

	@media (width <= 1024px) {
		margin-bottom: 140px;
	}

	@media (width <= 800px) {
		margin-bottom: 80px;
	}

	@media (width <= 660px) {
		margin-top: 104px;
	}

	@media (width <= 600px) {
		margin-top: 89px;
	}
`;

const ContentWrapper = styled.div`
	display: flex;
`;

const FIELDS_TO_POPULATE = [
	'partner',
	'partner.partnerTopLevel',
	'partner.logo',
	'partner.logo.file',
	'partner.servicesProvided',
	'countries',
	'industries',
	'integrations',
];

export async function getStaticProps({ params }) {
	const { lang, url } = params;

	if (!url) return { notFound: true };

	const localizedPartner = await fetchAPI('/m-partners', {
		url,
		lang,
		populate: FIELDS_TO_POPULATE,
	});
	let data = localizedPartner?.data?.[0];

	if (lang !== 'en') {
		const fallbackFields = await fetchAPI('/m-partners', {
			url,
			lang: 'en',
			populate: FIELDS_TO_POPULATE,
		});

		data = {
			...data,
			countries: fallbackFields?.data?.[0]?.countries,
			industries: fallbackFields?.data?.[0]?.industries,
			integrations: fallbackFields?.data?.[0]?.integrations,
			partner: {
				...data.partner,
				servicesProvided: fallbackFields?.data?.[0]?.partner?.servicesProvided,
			},
		};
	}

	return {
		props: {
			url,
			lang,
			data,
		},
	};
}

export async function getStaticPaths() {
	const entries = await fetchAPI('/m-partners');
	const paths: any = [];

	entries?.data?.forEach((entry) => {
		LANGUAGES.forEach((lang) => {
			paths.push({ params: { url: entry.url, lang: lang.code } });
		});
	});

	return {
		paths,
		fallback: false,
	};
}

export default MessagingPartnerPage;
